// Layers panel (DRAW_PLAN §1): layers top to bottom (frontmost first), each with a thumbnail, show/hide, lock,
// rename (double-click), and its objects nested inside (groups open like sub-layers). Click a row to select
// it (Shift adds), drag rows to reorder or move them into another layer or group, drag a layer header to
// reorder layers. A filled square marks a row that holds selected objects (the target indicator).
import { memo, useEffect, useRef, useState, type DragEvent } from 'react';
import { useT, type MessageKey } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { currentList } from '../../render/instance';
import {
  addLayer,
  moveLayer,
  moveNodesTo,
  removeLayer,
  setNodeFlags,
  updateLayer,
} from '../../commands/commands';
import type { Layer, Node } from '../../model/types';
import { paintItem } from '../../render/paint';

const MAX_ROWS = 300; // per layer; a 10,000-object layer lists the first rows and a count

const KIND: Record<Node['kind'], MessageKey> = {
  path: 'kindPath',
  shape: 'kindShape',
  text: 'kindText',
  image: 'kindImage',
  group: 'kindGroup',
  symbol: 'kindSymbol',
};

type Drag = { kind: 'nodes'; ids: string[] } | { kind: 'layer'; id: string };
let drag: Drag | null = null;

export function LayersDock() {
  const t = useT();
  const doc = useDesign((st) => st.doc)!;
  const sel = useDesignUi((st) => st.selection);
  const selected = new Set(sel);
  const layers = [...doc.layers].reverse(); // frontmost first, like Illustrator
  return (
    <div data-testid="layers-dock">
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <button
          data-testid="layer-add"
          onClick={() => useDesign.getState().run(addLayer(t('layerN', { n: doc.layers.length + 1 })))}
        >
          {t('newLayer')}
        </button>
        <button
          data-testid="layer-delete"
          disabled={doc.layers.length < 2}
          onClick={() => {
            const target =
              doc.layers.find((l) => l.children.some((c) => selected.has(c.id))) ??
              doc.layers[doc.layers.length - 1]!;
            useDesign.getState().run(removeLayer(target.id));
          }}
        >
          {t('deleteLayer')}
        </button>
      </div>
      {layers.map((l) => (
        <LayerRow key={l.id} layer={l} index={doc.layers.indexOf(l)} selected={selected} />
      ))}
    </div>
  );
}

function LayerRow({ layer, index, selected }: { layer: Layer; index: number; selected: Set<string> }) {
  const t = useT();
  const [open, setOpen] = useState(true);
  const [edit, setEdit] = useState(false);
  const target = layer.children.some((c) => selected.has(c.id));
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    const st = useDesign.getState();
    if (drag?.kind === 'layer') st.run(moveLayer(drag.id, index));
    else if (drag?.kind === 'nodes')
      st.run(moveNodesTo(drag.ids, { layerId: layer.id }, layer.children.length));
    drag = null;
  };
  return (
    <div style={{ borderTop: '1px solid var(--line)' }} data-testid={`layer-${layer.name}`}>
      <div
        draggable
        onDragStart={() => (drag = { kind: 'layer', id: layer.id })}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 0',
          background: 'rgba(255,255,255,0.04)',
        }}
      >
        <span style={{ width: 4, alignSelf: 'stretch', background: layer.color }} />
        <button
          aria-label={t(open ? 'collapse' : 'expand')}
          onClick={() => setOpen(!open)}
          style={{ width: 18, padding: 0 }}
        >
          {open ? '▾' : '▸'}
        </button>
        <Eye
          on={layer.visible}
          label={t('showHide')}
          onClick={() =>
            useDesign
              .getState()
              .run(updateLayer(layer.id, (l) => void (l.visible = !l.visible), 'cmdVisibility'))
          }
        />
        <Lock
          on={layer.locked}
          label={t('lockUnlock')}
          onClick={() =>
            useDesign.getState().run(updateLayer(layer.id, (l) => void (l.locked = !l.locked), 'cmdLock'))
          }
        />
        <LayerThumb layer={layer} />
        {edit ? (
          <input
            autoFocus
            defaultValue={layer.name}
            aria-label={t('layerName')}
            onBlur={(e) => {
              setEdit(false);
              const v = e.target.value.trim();
              if (v && v !== layer.name)
                useDesign.getState().run(updateLayer(layer.id, (l) => void (l.name = v), 'cmdRename'));
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            style={{ flex: 1, minWidth: 0 }}
          />
        ) : (
          <span onDoubleClick={() => setEdit(true)} style={{ flex: 1, fontWeight: 600 }}>
            {layer.name}
          </span>
        )}
        <span
          title={t('targetIndicator')}
          style={{
            width: 8,
            height: 8,
            background: target ? layer.color : 'transparent',
            border: `1px solid ${layer.color}`,
          }}
        />
      </div>
      {open && <NodeRows nodes={layer.children} layerId={layer.id} depth={1} selected={selected} />}
    </div>
  );
}

function NodeRows({
  nodes,
  layerId,
  depth,
  selected,
  groupId,
}: {
  nodes: Node[];
  layerId: string;
  depth: number;
  selected: Set<string>;
  groupId?: string;
}) {
  const t = useT();
  const shown = [...nodes].reverse().slice(0, MAX_ROWS);
  return (
    <>
      {shown.map((n) => (
        <NodeRow
          key={n.id}
          node={n}
          layerId={layerId}
          depth={depth}
          selected={selected}
          index={nodes.indexOf(n)}
          groupId={groupId}
        />
      ))}
      {nodes.length > MAX_ROWS && (
        <div style={{ paddingLeft: depth * 12, opacity: 0.6 }}>
          {t('moreObjects', { n: nodes.length - MAX_ROWS })}
        </div>
      )}
    </>
  );
}

const NodeRow = memo(function NodeRow({
  node,
  layerId,
  depth,
  selected,
  index,
  groupId,
}: {
  node: Node;
  layerId: string;
  depth: number;
  selected: Set<string>;
  index: number;
  groupId?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const isSel = selected.has(node.id);
  const select = (add: boolean) => {
    const ui = useDesignUi.getState();
    ui.set({
      selection: add
        ? isSel
          ? ui.selection.filter((s) => s !== node.id)
          : [...ui.selection, node.id]
        : [node.id],
    });
  };
  return (
    <>
      <div
        draggable
        data-testid={`node-row-${node.id}`}
        onDragStart={() =>
          (drag = { kind: 'nodes', ids: isSel ? useDesignUi.getState().selection : [node.id] })
        }
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (drag?.kind === 'nodes')
            useDesign.getState().run(moveNodesTo(drag.ids, { layerId, groupId }, index + 1));
          drag = null;
        }}
        onClick={(e) => depth === 1 && select(e.shiftKey)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          paddingLeft: depth * 12,
          background: isSel ? 'rgba(79,140,255,0.25)' : undefined,
          cursor: 'default',
        }}
      >
        {node.kind === 'group' ? (
          <button
            aria-label={t(open ? 'collapse' : 'expand')}
            onClick={(e) => (e.stopPropagation(), setOpen(!open))}
            style={{ width: 18, padding: 0 }}
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span style={{ width: 18 }} />
        )}
        <Eye
          on={node.visible}
          label={t('showHide')}
          onClick={() => useDesign.getState().run(setNodeFlags([node.id], { visible: !node.visible }))}
        />
        <Lock
          on={node.locked}
          label={t('lockUnlock')}
          onClick={() => useDesign.getState().run(setNodeFlags([node.id], { locked: !node.locked }))}
        />
        {edit ? (
          <input
            autoFocus
            defaultValue={node.name ?? ''}
            aria-label={t('objectName')}
            onBlur={(e) => {
              setEdit(false);
              const v = e.target.value.trim();
              if (v !== (node.name ?? '')) useDesign.getState().run(setNodeFlags([node.id], { name: v }));
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            style={{ flex: 1, minWidth: 0 }}
          />
        ) : (
          <span
            onDoubleClick={() => setEdit(true)}
            style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {node.name || t(KIND[node.kind])}
          </span>
        )}
      </div>
      {node.kind === 'group' && open && (
        <NodeRows
          nodes={node.children}
          layerId={layerId}
          depth={depth + 1}
          selected={selected}
          groupId={node.id}
        />
      )}
    </>
  );
});

const Eye = ({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) => (
  <button
    aria-label={label}
    aria-pressed={on}
    onClick={(e) => (e.stopPropagation(), onClick())}
    style={{ width: 20, padding: 0, opacity: on ? 1 : 0.35 }}
  >
    👁
  </button>
);
const Lock = ({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) => (
  <button
    aria-label={label}
    aria-pressed={on}
    onClick={(e) => (e.stopPropagation(), onClick())}
    style={{ width: 20, padding: 0, opacity: on ? 1 : 0.35 }}
  >
    {on ? '🔒' : '🔓'}
  </button>
);

/** A small drawing of the layer's objects over the artboards' area, refreshed at most twice a second. */
function LayerThumb({ layer }: { layer: Layer }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const version = useDesign((st) => st.version);
  useEffect(() => {
    const id = window.setTimeout(() => {
      const c = ref.current;
      const doc = useDesign.getState().doc;
      if (!c || !doc) return;
      const g = c.getContext('2d')!;
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.fillStyle = '#fff';
      g.fillRect(0, 0, c.width, c.height);
      const ab = doc.artboards;
      const x0 = Math.min(...ab.map((a) => a.x)),
        y0 = Math.min(...ab.map((a) => a.y)),
        x1 = Math.max(...ab.map((a) => a.x + a.w)),
        y1 = Math.max(...ab.map((a) => a.y + a.h));
      const k = Math.min(c.width / (x1 - x0), c.height / (y1 - y0));
      g.setTransform(k, 0, 0, k, -x0 * k, -y0 * k);
      let n = 0;
      for (const it of currentList().items) {
        if (it.layerId !== layer.id || it.hidden) continue;
        paintItem(g, it, doc);
        if (++n > 2000) break; // a thumbnail does not need every object of a huge layer
      }
    }, 500);
    return () => clearTimeout(id);
  }, [version, layer]);
  return <canvas ref={ref} width={32} height={22} style={{ border: '1px solid var(--line)' }} />;
}
